/**
 * Type-safe, hierarchical query key factory.
 *
 * All React Query keys MUST be defined here — never use raw string arrays.
 * Keys are hierarchical so you can invalidate broadly (e.g., guilds.all)
 * or narrowly (e.g., guilds.detail('gld_1')).
 *
 * MIGRATION IN PROGRESS — per-arche namespaces are moving into their
 * arche's own `api/queryKeys.ts`. The central object below is becoming a
 * thin re-export aggregator. See
 * docs/audits/social-media-modularity-audit.md §10 PW-D. Social-media has
 * been migrated as the pilot; other arches will follow.
 */
import { socialMediaQueryKeys } from '@/arche/arches/social-media/api/queryKeys';

export const queryKeys = {
  // === Guilds ===
  guilds: {
    all: ['guilds'] as const,
    lists: () => [...queryKeys.guilds.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.guilds.lists(), filters] as const,
    details: () => [...queryKeys.guilds.all, 'detail'] as const,
    detail: (guildId: string) => [...queryKeys.guilds.details(), guildId] as const,
    members: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'members'] as const,
    activity: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'activity'] as const,
    invites: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'invites'] as const,
    bans: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'bans'] as const,
    treasury: (guildId: string) => [...queryKeys.guilds.detail(guildId), 'treasury'] as const,
    my: () => [...queryKeys.guilds.all, 'my'] as const,
  },

  // === Game Rewards ===
  gameRewards: {
    all: ['gameRewards'] as const,
    gameConfigs: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.gameRewards.all, 'gameConfigs', filters] as const)
        : ([...queryKeys.gameRewards.all, 'gameConfigs'] as const),
    subscription: () => [...queryKeys.gameRewards.all, 'subscription'] as const,
    subscriptionTiers: () => [...queryKeys.gameRewards.all, 'subscriptionTiers'] as const,
    timePlayed: () => [...queryKeys.gameRewards.all, 'timePlayed'] as const,
    rewardHistory: () => [...queryKeys.gameRewards.all, 'rewardHistory'] as const,
    rewardLog: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.gameRewards.all, 'rewardLog', filters] as const)
        : ([...queryKeys.gameRewards.all, 'rewardLog'] as const),
    rewardRules: () => [...queryKeys.gameRewards.all, 'rewardRules'] as const,
    rewardTypeLimits: () => [...queryKeys.gameRewards.all, 'rewardTypeLimits'] as const,
    leaderboard: (gameConfigId: string, gameEvent: string) =>
      [...queryKeys.gameRewards.all, 'leaderboard', gameConfigId, gameEvent] as const,
    leaderboardGames: () => [...queryKeys.gameRewards.all, 'leaderboardGames'] as const,
    adminSubscriptions: () => [...queryKeys.gameRewards.all, 'adminSubscriptions'] as const,
    storeProducts: () => [...queryKeys.gameRewards.all, 'storeProducts'] as const,
    claimRejections: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameRewards.all, 'claimRejections', filters ?? {}] as const,
    itemDefsByIds: (ids: readonly string[]) =>
      [...queryKeys.gameRewards.all, 'itemDefsByIds', [...ids].sort().join(',')] as const,
  },

  // === Chat ===
  chat: {
    all: ['chat'] as const,
    channels: () => [...queryKeys.chat.all, 'channels'] as const,
    channel: (channelId: string) => [...queryKeys.chat.channels(), channelId] as const,
    channelMembers: (channelId: string) =>
      [...queryKeys.chat.channel(channelId), 'members'] as const,
    settings: () => [...queryKeys.chat.all, 'settings'] as const,
  },

  // === DFE ===
  dfe: {
    all: ['dfe'] as const,
    definitions: () => [...queryKeys.dfe.all, 'definitions'] as const,
    definitionList: (filters: Record<string, unknown>) =>
      [...queryKeys.dfe.definitions(), filters] as const,
    definition: (itemDefId: string) => [...queryKeys.dfe.definitions(), itemDefId] as const,
    inventory: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'inventory', filters] as const,
    transferHistory: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'transferHistory', filters] as const,
    adminTransfers: (filters?: Record<string, unknown>) =>
      [...queryKeys.dfe.all, 'adminTransfers', filters] as const,
  },

  // === LitSquad ===
  litSquad: {
    all: ['litSquad'] as const,
    definitions: () => [...queryKeys.litSquad.all, 'definitions'] as const,
    definitionList: (filters: Record<string, unknown>) =>
      [...queryKeys.litSquad.definitions(), filters] as const,
    definition: (itemDefId: string) => [...queryKeys.litSquad.definitions(), itemDefId] as const,
    inventory: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'inventory', filters] as const,
    item: (itemId: string) => [...queryKeys.litSquad.all, 'item', itemId] as const,
    transferHistory: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'transferHistory', filters] as const,
    adminTransfers: (filters?: Record<string, unknown>) =>
      [...queryKeys.litSquad.all, 'adminTransfers', filters] as const,
    craft: () => [...queryKeys.litSquad.all, 'craft'] as const,
    craftIngredients: () => [...queryKeys.litSquad.craft(), 'ingredients'] as const,
  },

  // === DFE Game State ===
  gameState: {
    all: ['gameState'] as const,
    detail: (keyword: string) => [...queryKeys.gameState.all, 'detail', keyword] as const,
    list: () => [...queryKeys.gameState.all, 'list'] as const,
    admin: () => [...queryKeys.gameState.all, 'admin'] as const,
    adminDetail: (keyword: string) => [...queryKeys.gameState.admin(), keyword] as const,
    adminList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'list', filters] as const,
    adminAuditLog: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.admin(), 'auditLog', filters] as const,
    grants: () => [...queryKeys.gameState.admin(), 'grants'] as const,
    grantsList: (filters?: Record<string, unknown>) =>
      [...queryKeys.gameState.grants(), 'list', filters] as const,
    grantDetail: (archeId: string, resourceType: string, resourceName: string) =>
      [...queryKeys.gameState.grants(), 'detail', archeId, resourceType, resourceName] as const,
  },

  // === DFE Inventory Transition ===
  inventoryTransition: {
    all: ['inventoryTransition'] as const,
    importHistory: () => [...queryKeys.inventoryTransition.all, 'importHistory'] as const,
    adminImports: () => [...queryKeys.inventoryTransition.all, 'adminImports'] as const,
  },

  // === Crafting ===
  crafting: {
    all: ['crafting'] as const,
    recipes: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'recipes', filters] as const,
    recipeDetail: (recipeId: string) => [...queryKeys.crafting.all, 'recipe', recipeId] as const,
    learnedRecipes: (userId?: string) =>
      [...queryKeys.crafting.all, 'learnedRecipes', userId] as const,
    log: (filters?: Record<string, unknown>) =>
      [...queryKeys.crafting.all, 'log', filters] as const,
  },

  // === Favorites ===
  favorites: {
    all: ['favorites'] as const,
    list: () => [...queryKeys.favorites.all, 'list'] as const,
    status: (archId: string) => [...queryKeys.favorites.all, 'status', archId] as const,
  },

  // === Polls ===
  polls: {
    all: ['polls'] as const,
    list: (filters?: { status?: string }) =>
      [...queryKeys.polls.all, 'list', { status: filters?.status ?? null }] as const,
    detail: (pollId: string) => [...queryKeys.polls.all, 'detail', pollId] as const,
    results: (pollId: string) => [...queryKeys.polls.detail(pollId), 'results'] as const,
    myResponse: (pollId: string) => [...queryKeys.polls.detail(pollId), 'myResponse'] as const,
  },

  // === Liquidity Caches ===
  liquidityCaches: {
    all: ['liquidityCaches'] as const,
    pools: () => [...queryKeys.liquidityCaches.all, 'pools'] as const,
    pool: (poolId: string) => [...queryKeys.liquidityCaches.pools(), poolId] as const,
    positions: () => [...queryKeys.liquidityCaches.all, 'positions'] as const,
    quote: (poolId: string, params: Record<string, unknown>) =>
      [...queryKeys.liquidityCaches.pool(poolId), 'quote', params] as const,
  },

  // === Mining ===
  mining: {
    all: ['mining'] as const,
    lands: () => [...queryKeys.mining.all, 'lands'] as const,
    log: () => [...queryKeys.mining.all, 'log'] as const,
  },

  // === Plugin Builder ===
  pluginBuilder: {
    all: ['pluginBuilder'] as const,
    sessions: () => [...queryKeys.pluginBuilder.all, 'sessions'] as const,
    publishStatus: (sessionId: string) =>
      [...queryKeys.pluginBuilder.all, 'publishStatus', sessionId] as const,
  },

  // === Plugin submissions (dev console) ===
  submissions: {
    all: ['submissions'] as const,
    myList: () => [...queryKeys.submissions.all, 'mine'] as const,
    detail: (submissionId: string) =>
      [...queryKeys.submissions.all, 'detail', submissionId] as const,
    escalated: () => [...queryKeys.submissions.all, 'admin', 'escalated'] as const,
  },

  // === Contributor portal — arche detail ===
  contributorArches: {
    all: ['contributor', 'arches'] as const,
    detail: (archeId: string) => [...queryKeys.contributorArches.all, 'detail', archeId] as const,
    libraryConfig: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'library-config'] as const,
    billingSummary: (archeId: string) =>
      [...queryKeys.contributorArches.detail(archeId), 'billing-summary'] as const,
    assets: (archeId: string, opts?: Record<string, unknown>) =>
      [...queryKeys.contributorArches.detail(archeId), 'assets', opts ?? {}] as const,
  },

  // === Learn Anything ===
  learnAnything: {
    all: ['learnAnything'] as const,
    courses: () => [...queryKeys.learnAnything.all, 'courses'] as const,
    curriculum: (courseId: string) =>
      [...queryKeys.learnAnything.courses(), courseId, 'curriculum'] as const,
    progress: (courseId: string) =>
      [...queryKeys.learnAnything.courses(), courseId, 'progress'] as const,
  },

  // === Learn About Fias ===
  learnAboutFias: {
    all: ['learnAboutFias'] as const,
    resources: () => [...queryKeys.learnAboutFias.all, 'resources'] as const,
    admin: () => [...queryKeys.learnAboutFias.all, 'admin'] as const,
    adminDocuments: (filters?: Record<string, unknown>) =>
      [...queryKeys.learnAboutFias.admin(), 'documents', filters] as const,
    adminDocument: (docId: string) =>
      [...queryKeys.learnAboutFias.admin(), 'document', docId] as const,
    adminStats: () => [...queryKeys.learnAboutFias.admin(), 'stats'] as const,
  },

  // === Models ===
  models: {
    all: ['models'] as const,
    list: () => [...queryKeys.models.all, 'list'] as const,
    definition: (modelId: string) => [...queryKeys.models.all, 'definition', modelId] as const,
    definitionByLookup: (provider: string, modelName: string) =>
      [...queryKeys.models.all, 'definition', provider, modelName] as const,
    imageModels: () => [...queryKeys.models.all, 'imageModels'] as const,
  },

  // === Conversations ===
  conversations: {
    all: ['conversations'] as const,
    list: (archeId: string) => [...queryKeys.conversations.all, 'list', archeId] as const,
    detail: (conversationId: string) =>
      [...queryKeys.conversations.all, 'detail', conversationId] as const,
  },

  // === Files & Storage ===
  files: {
    all: ['files'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.files.all, 'list', filters] as const,
    detail: (fileId: string) => [...queryKeys.files.all, 'detail', fileId] as const,
    downloadUrl: (fileId: string) => [...queryKeys.files.all, 'downloadUrl', fileId] as const,
  },

  // === Contact Book ===
  contactBook: {
    all: ['contact-book'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.contactBook.all, 'list', filters] as const,
  },

  folders: {
    all: ['folders'] as const,
    list: (parentId?: string | null) => [...queryKeys.folders.all, 'list', parentId] as const,
    detail: (folderId: string) => [...queryKeys.folders.all, 'detail', folderId] as const,
    breadcrumb: (folderId: string) => [...queryKeys.folders.all, 'breadcrumb', folderId] as const,
  },

  storage: {
    all: ['storage'] as const,
    usage: () => [...queryKeys.storage.all, 'usage'] as const,
    operations: () => [...queryKeys.storage.all, 'operations'] as const,
  },

  // === DFE Avatars (storage) ===
  dfeAvatars: {
    all: ['dfeAvatars'] as const,
    groups: () => [...queryKeys.dfeAvatars.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.dfeAvatars.all, 'files', prefix] as const,
  },

  // === DFE Icons (storage) ===
  dfeIcons: {
    all: ['dfeIcons'] as const,
    groups: () => [...queryKeys.dfeIcons.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.dfeIcons.all, 'files', prefix] as const,
  },

  // === Litcraft Addressables (storage) ===
  litcraftAddressables: {
    all: ['litcraftAddressables'] as const,
    groups: () => [...queryKeys.litcraftAddressables.all, 'groups'] as const,
    files: (prefix: string) => [...queryKeys.litcraftAddressables.all, 'files', prefix] as const,
  },

  // Owned by the social-media arche; see
  // client/src/arche/arches/social-media/api/queryKeys.ts
  socialMedia: socialMediaQueryKeys,

  // === Todo Lists (W6 — Vault-resident; keyed by holderId) ===
  todoLists: {
    all: ['todoLists'] as const,
    details: () => [...queryKeys.todoLists.all, 'detail'] as const,
    detail: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.details(), holderId, listId] as const,
    my: (holderId: string) => [...queryKeys.todoLists.all, 'my', holderId] as const,
    workspaceSummary: () => [...queryKeys.todoLists.all, 'workspaceSummary'] as const,
    sections: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'sections'] as const,
    items: (holderId: string, listId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'items', filters] as const,
    completed: (holderId: string, listId: string) =>
      [...queryKeys.todoLists.detail(holderId, listId), 'completed'] as const,
    public: (slug: string) => [...queryKeys.todoLists.all, 'public', slug] as const,
    publicCompleted: (slug: string) =>
      [...queryKeys.todoLists.all, 'public', slug, 'completed'] as const,
  },

  // === Customer Support ===
  customerSupport: {
    all: ['customerSupport'] as const,
    tickets: () => [...queryKeys.customerSupport.all, 'tickets'] as const,
    ticketList: (filters?: Record<string, unknown>) =>
      [...queryKeys.customerSupport.tickets(), 'list', filters] as const,
    ticketDetail: (ticketId: string) => [...queryKeys.customerSupport.tickets(), ticketId] as const,
    ticketMessages: (ticketId: string) =>
      [...queryKeys.customerSupport.ticketDetail(ticketId), 'messages'] as const,
    faqs: (categoryId?: string) => [...queryKeys.customerSupport.all, 'faqs', categoryId] as const,
    faqSearch: (query: string) =>
      [...queryKeys.customerSupport.all, 'faqs', 'search', query] as const,
    categories: () => [...queryKeys.customerSupport.all, 'categories'] as const,
    admin: () => [...queryKeys.customerSupport.all, 'admin'] as const,
    adminTickets: (filters?: Record<string, unknown>) =>
      [...queryKeys.customerSupport.admin(), 'tickets', filters] as const,
    adminTicketDetail: (ticketId: string) =>
      [...queryKeys.customerSupport.admin(), 'ticket', ticketId] as const,
    adminTicketMessages: (ticketId: string) =>
      [...queryKeys.customerSupport.adminTicketDetail(ticketId), 'messages'] as const,
    adminAnalytics: () => [...queryKeys.customerSupport.admin(), 'analytics'] as const,
    adminCategories: () => [...queryKeys.customerSupport.admin(), 'categories'] as const,
    adminCannedResponses: () => [...queryKeys.customerSupport.admin(), 'cannedResponses'] as const,
    adminTags: () => [...queryKeys.customerSupport.admin(), 'tags'] as const,
  },

  // === Voice Studio ===
  voiceStudio: {
    all: ['voiceStudio'] as const,
    voiceModels: () => [...queryKeys.voiceStudio.all, 'voiceModels'] as const,
    platformVoices: () => [...queryKeys.voiceStudio.all, 'platformVoices'] as const,
  },

  // === Document Sign ===
  documentSign: {
    all: ['documentSign'] as const,
    envelopes: () => [...queryKeys.documentSign.all, 'envelopes'] as const,
    envelopeList: (filters: Record<string, unknown>) =>
      [...queryKeys.documentSign.envelopes(), 'list', filters] as const,
    envelope: (envelopeId: string) => [...queryKeys.documentSign.envelopes(), envelopeId] as const,
    files: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'files'] as const,
    recipients: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'recipients'] as const,
    fields: (envelopeId: string, fileId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'files', fileId, 'fields'] as const,
    events: (envelopeId: string) =>
      [...queryKeys.documentSign.envelope(envelopeId), 'events'] as const,
    signingSession: (token: string) => [...queryKeys.documentSign.all, 'signing', token] as const,
    recentRecipients: (holderId: string) =>
      [...queryKeys.documentSign.all, 'recent-recipients', holderId] as const,
  },

  // === Fias Leads (Phase 9 — Holder-scoped CRM on Vault substrate) ===
  fiasLeads: {
    all: ['fiasLeads'] as const,
    forHolder: (holderId: string) => [...queryKeys.fiasLeads.all, holderId] as const,
    companies: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'companies'] as const,
    company: (holderId: string, companyId: string) =>
      [...queryKeys.fiasLeads.companies(holderId), companyId] as const,
    contacts: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'contacts'] as const,
    contactsList: (holderId: string, filters: { companyId?: string; includeArchived?: boolean }) =>
      [...queryKeys.fiasLeads.contacts(holderId), 'list', filters] as const,
    contact: (holderId: string, contactId: string) =>
      [...queryKeys.fiasLeads.contacts(holderId), contactId] as const,
    relationships: (holderId: string, contactId: string) =>
      [...queryKeys.fiasLeads.contact(holderId, contactId), 'relationships'] as const,
    deals: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'deals'] as const,
    dealsList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.deals(holderId), 'list', filters] as const,
    deal: (holderId: string, dealId: string) =>
      [...queryKeys.fiasLeads.deals(holderId), dealId] as const,
    pipelineSummary: (holderId: string, pipelineId: string) =>
      [...queryKeys.fiasLeads.deals(holderId), 'pipelineSummary', pipelineId] as const,
    pipelines: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'pipelines'] as const,
    pipeline: (holderId: string, pipelineId: string) =>
      [...queryKeys.fiasLeads.pipelines(holderId), pipelineId] as const,
    tasks: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'tasks'] as const,
    tasksList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.tasks(holderId), 'list', filters] as const,
    task: (holderId: string, taskId: string) =>
      [...queryKeys.fiasLeads.tasks(holderId), taskId] as const,
    activities: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'activities'] as const,
    activitiesList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.activities(holderId), 'list', filters] as const,
    activity: (holderId: string, activityId: string) =>
      [...queryKeys.fiasLeads.activities(holderId), activityId] as const,
    emailTemplates: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'emailTemplates'] as const,
    emailTemplate: (holderId: string, templateId: string) =>
      [...queryKeys.fiasLeads.emailTemplates(holderId), templateId] as const,
    automations: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'automations'] as const,
    automation: (holderId: string, ruleId: string) =>
      [...queryKeys.fiasLeads.automations(holderId), ruleId] as const,
    automationExecutions: (holderId: string, ruleId: string) =>
      [...queryKeys.fiasLeads.automation(holderId, ruleId), 'executions'] as const,
    customFields: (holderId: string, entityType?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'customFields', entityType ?? '_any'] as const,
    savedViews: (holderId: string, entityType?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'savedViews', entityType ?? '_any'] as const,
    dashboard: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dashboard', pipelineId ?? '_default'] as const,
    dealRisk: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dealRisk'] as const,
    dealRiskAlerts: (holderId: string) =>
      [...queryKeys.fiasLeads.dealRisk(holderId), 'alerts'] as const,
    timeline: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'timeline', filters] as const,
    reports: (holderId: string) => [...queryKeys.fiasLeads.forHolder(holderId), 'reports'] as const,
    pipelineVelocity: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'pipelineVelocity', pipelineId ?? '_all'] as const,
    forecast: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'forecast', pipelineId ?? '_all'] as const,
    activityTrends: (holderId: string, days: number) =>
      [...queryKeys.fiasLeads.reports(holderId), 'activityTrends', days] as const,
    lostDeals: (holderId: string, pipelineId?: string) =>
      [...queryKeys.fiasLeads.reports(holderId), 'lostDeals', pipelineId ?? '_all'] as const,
    dedupContacts: (holderId: string) =>
      [...queryKeys.fiasLeads.forHolder(holderId), 'dedup', 'contacts'] as const,
    crossArchePresence: (holderId: string, contactIds: string[], companyIds: string[]) =>
      [
        ...queryKeys.fiasLeads.forHolder(holderId),
        'crossArchePresence',
        contactIds.sort().join(','),
        companyIds.sort().join(','),
      ] as const,
  },

  // === Vault Notes (Phase 3 — first Vault Entity consumer) ===
  vaultNotes: {
    all: ['vaultNotes'] as const,
    lists: () => [...queryKeys.vaultNotes.all, 'list'] as const,
    list: (filters: { includeDeleted?: boolean; limit?: number }) =>
      [...queryKeys.vaultNotes.lists(), filters] as const,
    details: () => [...queryKeys.vaultNotes.all, 'detail'] as const,
    detail: (noteId: string) => [...queryKeys.vaultNotes.details(), noteId] as const,
  },

  // === Vault — caller-side (membership picker, invitations) ===
  //
  // Folded in from previously module-local TEAM_KEY / MY_INVITES_KEY /
  // MY_MEMBERSHIPS_KEY constants in arche/arches/fias360/hooks/useVaultTeam.ts
  // and the inline `['vault','me','memberships']` literal in
  // components/HolderSwitcher.tsx. Centralizing here so the namespace
  // ownership is unambiguous and renames don't silently miss callers.
  vault: {
    all: ['vault'] as const,
    me: {
      memberships: () => ['vault', 'me', 'memberships'] as const,
      invitations: () => ['vault', 'me', 'invitations'] as const,
    },
    team: {
      all: ['vault', 'team'] as const,
      forHolder: (holderId: string) => ['vault', 'team', holderId] as const,
      members: (holderId: string) => ['vault', 'team', holderId, 'members'] as const,
      invitations: (holderId: string) => ['vault', 'team', holderId, 'invitations'] as const,
    },
  },

  // === Fias 360 (Phase 10 — SMB CRM arche) ===
  fias360: {
    all: ['fias360'] as const,
    customers: (holderId: string) => [...queryKeys.fias360.all, 'customers', holderId] as const,
    /** Filtered list — separate cache slot per filter object so a
     * companyId-filtered query on AccountDetailPage doesn't poison the
     * unfiltered list on the Members tab. Invalidation can still use
     * `customers(holderId)` as a prefix to clear all filter variants. */
    customersList: (holderId: string, filters: { companyId?: string; includeArchived?: boolean }) =>
      [...queryKeys.fias360.all, 'customers', holderId, 'list', filters] as const,
    customer: (holderId: string, customerId: string) =>
      [...queryKeys.fias360.customers(holderId), customerId] as const,
    companies: (holderId: string) => [...queryKeys.fias360.all, 'companies', holderId] as const,
    company: (holderId: string, companyId: string) =>
      [...queryKeys.fias360.companies(holderId), companyId] as const,
    timeline: (
      holderId: string,
      filters: {
        regardingType?: string;
        regardingId?: string;
        kind?: string;
        limit?: number;
      },
    ) => [...queryKeys.fias360.all, 'timeline', holderId, filters] as const,
    health: (holderId: string, subjectKind: string, subjectId: string) =>
      [...queryKeys.fias360.all, 'health', holderId, subjectKind, subjectId] as const,
    renewals: (holderId: string, filters: { companyId?: string; status?: string }) =>
      [...queryKeys.fias360.all, 'renewals', holderId, filters] as const,
    tasks: (holderId: string) => [...queryKeys.fias360.all, 'tasks', holderId] as const,
    tasksList: (holderId: string, filters: Record<string, unknown>) =>
      [...queryKeys.fias360.tasks(holderId), 'list', filters] as const,
    task: (holderId: string, taskId: string) =>
      [...queryKeys.fias360.tasks(holderId), taskId] as const,
    // Segments — folded in from a module-local SEGMENTS_KEY_BASE
    // in useFias360.ts. Same pattern as the customers/companies
    // namespaces: per-Holder root, filtered list, individual detail.
    segments: (holderId: string) => [...queryKeys.fias360.all, 'segments', holderId] as const,
    segmentsList: (holderId: string, options?: Record<string, unknown>) =>
      [...queryKeys.fias360.segments(holderId), options] as const,
    segment: (holderId: string, segmentId: string) =>
      [...queryKeys.fias360.segments(holderId), 'detail', segmentId] as const,
    segmentApply: (holderId: string, segmentId: string) =>
      [...queryKeys.fias360.segments(holderId), 'apply', segmentId] as const,
    // Reports — folded in from REPORTS_KEY_BASE.
    reports: (holderId: string) => [...queryKeys.fias360.all, 'reports', holderId] as const,
    reportCustomersByLifecycle: (holderId: string) =>
      [...queryKeys.fias360.reports(holderId), 'customers-by-lifecycle-stage'] as const,
    reportActivityVolume: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'activity-volume-by-actor', days] as const,
    reportUncontactedCustomers: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'uncontacted-customers', days] as const,
    reportNewCustomers: (holderId: string, days: number) =>
      [...queryKeys.fias360.reports(holderId), 'new-customers-over-time', days] as const,
    reportRenewalsByStatus: (holderId: string) =>
      [...queryKeys.fias360.reports(holderId), 'renewals-by-status'] as const,
    // Custom fields — folded in from CUSTOM_FIELDS_KEY_BASE.
    customFields: (holderId: string) =>
      [...queryKeys.fias360.all, 'custom-fields', holderId] as const,
    customFieldDefs: (holderId: string, options?: Record<string, unknown>) =>
      [...queryKeys.fias360.customFields(holderId), 'defs', options] as const,
    customFieldDefsRoot: (holderId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'defs'] as const,
    customFieldValuesForCustomer: (holderId: string, customerId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'values', 'customer', customerId] as const,
    customFieldValuesForCompany: (holderId: string, companyId: string) =>
      [...queryKeys.fias360.customFields(holderId), 'values', 'company', companyId] as const,
    // Workspace preferences — folded in from WORKSPACE_PREFS_KEY_BASE.
    workspacePreferences: (holderId: string) =>
      [...queryKeys.fias360.all, 'workspace-preferences', holderId] as const,
  },

  // === Vault Documents (Phase 12 — Holder-scoped document storage) ===
  vaultDocuments: {
    all: ['vaultDocuments'] as const,
    /** Per-Holder root. Use as the invalidation prefix when anything
     * about a Holder's document corpus changes (upload finalize,
     * archive, delete, restore, sensitivity change). */
    forHolder: (holderId: string) => [...queryKeys.vaultDocuments.all, holderId] as const,
    /** Filtered list — separate cache slot per filter object so an
     * archive-tab listing doesn't poison the default active list. */
    list: (
      holderId: string,
      filters: {
        status?: string;
        tags?: string[];
        includeArchived?: boolean;
        includeDeleted?: boolean;
        deletedOnly?: boolean;
        folderId?: string | null;
      },
    ) => [...queryKeys.vaultDocuments.forHolder(holderId), 'list', filters] as const,
    /** Folder tree for a Holder. Single cache slot per Holder — the
     * tree is small enough that we always fetch the whole flat list
     * and assemble the tree client-side. */
    folders: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'folders'] as const,
    details: () => [...queryKeys.vaultDocuments.all, 'detail'] as const,
    detail: (documentId: string) => [...queryKeys.vaultDocuments.details(), documentId] as const,
    versions: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'versions'] as const,
    /** Search results — keyed by the query so a re-search with the
     * same string hits the cache. Frontend invalidates this whole
     * branch on any doc write. */
    search: (holderId: string, params: { query: string; topK?: number; minSimilarity?: number }) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'search', params] as const,
    /** Per-Holder storage rollup (review fix #9). */
    storage: (holderId: string) =>
      [...queryKeys.vaultDocuments.forHolder(holderId), 'storage'] as const,
    /** Cross-arche attachment views (Phase 12e-1). */
    attachmentsForDocument: (documentId: string) =>
      [...queryKeys.vaultDocuments.detail(documentId), 'attachments'] as const,
    attachmentsForTarget: (regardingType: string, regardingId: string) =>
      [
        ...queryKeys.vaultDocuments.all,
        'attachmentsForTarget',
        regardingType,
        regardingId,
      ] as const,
  },

  // === Email ===
  email: {
    all: ['email'] as const,
    accounts: () => [...queryKeys.email.all, 'accounts'] as const,
    messages: (params?: Record<string, unknown>) =>
      [...queryKeys.email.all, 'messages', params] as const,
    messageDetail: (messageId: string) =>
      [...queryKeys.email.all, 'messages', 'detail', messageId] as const,
    threads: (params?: Record<string, unknown>) =>
      [...queryKeys.email.all, 'threads', params] as const,
    threadDetail: (threadId: string) =>
      [...queryKeys.email.all, 'threads', 'detail', threadId] as const,
    folders: (accountId: string) => [...queryKeys.email.all, 'folders', accountId] as const,
    labels: (accountId: string) => [...queryKeys.email.all, 'labels', accountId] as const,
    contacts: (params?: Record<string, unknown>) =>
      [...queryKeys.email.all, 'contacts', params] as const,
    contactGroups: (accountId: string) =>
      [...queryKeys.email.all, 'contactGroups', accountId] as const,
    filters: (accountId: string) => [...queryKeys.email.all, 'filters', accountId] as const,
    templates: (params?: Record<string, unknown>) =>
      [...queryKeys.email.all, 'templates', params] as const,
    snippets: (accountId: string) => [...queryKeys.email.all, 'snippets', accountId] as const,
    signatures: (accountId: string) => [...queryKeys.email.all, 'signatures', accountId] as const,
    search: (params?: Record<string, unknown>) =>
      [...queryKeys.email.all, 'search', params] as const,
    screener: (accountId: string, status?: string) =>
      [...queryKeys.email.all, 'screener', accountId, status] as const,
    scheduled: (accountId: string) => [...queryKeys.email.all, 'scheduled', accountId] as const,
    snoozed: (accountId: string) => [...queryKeys.email.all, 'snoozed', accountId] as const,
  },

  // === Travel ===
  travel: {
    all: ['travel'] as const,
    my: () => [...queryKeys.travel.all, 'my'] as const,
    details: () => [...queryKeys.travel.all, 'detail'] as const,
    detail: (tripId: string) => [...queryKeys.travel.details(), tripId] as const,
    profile: () => [...queryKeys.travel.all, 'profile'] as const,
    features: () => [...queryKeys.travel.all, 'features'] as const,
    dossier: (tripId: string) => [...queryKeys.travel.all, 'dossier', tripId] as const,
    inbox: (filters?: Record<string, unknown>) =>
      [...queryKeys.travel.all, 'inbox', filters] as const,
    flightStatus: (itemId: string) => [...queryKeys.travel.all, 'flightStatus', itemId] as const,
    budget: (tripId: string) => [...queryKeys.travel.all, 'budget', tripId] as const,
    budgetSummary: (tripId: string) => [...queryKeys.travel.all, 'budgetSummary', tripId] as const,
    expenses: (tripId: string) => [...queryKeys.travel.all, 'expenses', tripId] as const,
    todos: (tripId: string, category?: string) =>
      [...queryKeys.travel.all, 'todos', tripId, category ?? null] as const,
    shareLinks: (tripId: string) => [...queryKeys.travel.all, 'shareLinks', tripId] as const,
    shareLinksAll: () => [...queryKeys.travel.all, 'shareLinks', 'all'] as const,
    sharedTrip: (shareId: string) => [...queryKeys.travel.all, 'sharedTrip', shareId] as const,
  },

  // === Calendar Sync ===
  calendarSync: {
    all: ['calendarSync'] as const,
    connections: () => [...queryKeys.calendarSync.all, 'connections'] as const,
    remoteCalendars: (connectionId: string) =>
      [...queryKeys.calendarSync.all, 'remoteCalendars', connectionId] as const,
    jobs: () => [...queryKeys.calendarSync.all, 'jobs'] as const,
    job: (jobId: string) => [...queryKeys.calendarSync.jobs(), jobId] as const,
  },

  // === Contact Sync ===
  contactSync: {
    all: ['contactSync'] as const,
    connections: () => [...queryKeys.contactSync.all, 'connections'] as const,
    remoteGroups: (connectionId: string) =>
      [...queryKeys.contactSync.all, 'remoteGroups', connectionId] as const,
    jobs: () => [...queryKeys.contactSync.all, 'jobs'] as const,
    job: (jobId: string) => [...queryKeys.contactSync.jobs(), jobId] as const,
  },
} as const;
